<template>
    <v-card>
        <v-card-title>
            <v-text-field
                v-model.trim="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
            ></v-text-field>
        </v-card-title>
        <v-data-table
            :headers="headers"
            :items="probesList"
            :items-per-page="20"
            class="elevation-1"
            :search="search"
            :loading="loading"
            loading-text="Loading stations"
            :custom-filter="customFilter"
            :page="tableOptions && tableOptions.page ? tableOptions.page : 1"
            :options.sync="options"
            :footer-props="footerProps"
            :server-items-length="probesCount"
        >
            <template v-slot:header.status="{}">
                <v-menu v-model="showStatusMenu" :close-on-content-click="false" offset-y>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" style="cursor: pointer; white-space: nowrap;">{{
                            `PT State (${selectedStatus})`
                        }}</span>
                    </template>
                    <v-card class="px-5 py-2" style="z-index: 10">
                        <v-row>
                            <v-radio-group v-model="selectedStatus">
                                <v-radio
                                    v-for="status in statuses"
                                    :key="status"
                                    :label="`${status.charAt(0).toUpperCase() + status.slice(1)}`"
                                    :value="status"
                                    @click="
                                        handleFilterStatusesClick()
                                        showStatusMenu = false
                                    "
                                >
                                    >
                                </v-radio>
                            </v-radio-group>
                        </v-row>
                    </v-card>
                </v-menu>
            </template>
            <template v-slot:header.latest_state="{}">
                <v-menu v-model="showStateMenu" :close-on-content-click="false" offset-y>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" style="cursor: pointer; white-space: nowrap;">{{
                            `PT Condition (${selectedState})`
                        }}</span>
                    </template>
                    <v-card class="px-5 py-2" style="z-index: 10">
                        <v-row>
                            <v-radio-group v-model="selectedState">
                                <v-radio
                                    v-for="state in states"
                                    :key="state.value"
                                    :label="state.name"
                                    :value="state.value"
                                    @click="
                                        handleFilterStatesClick()
                                        showStateMenu = false
                                    "
                                >
                                    >
                                </v-radio>
                            </v-radio-group>
                        </v-row>
                    </v-card>
                </v-menu>
            </template>
            <template v-slot:header.latest_country="{}">
                <v-menu v-model="showCountryMenu" :close-on-content-click="false" offset-y>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" style="cursor: pointer; white-space: nowrap;">
                            {{ `Country (${selectedCountries && selectedCountries.length > 0 ? selectedCountries.join(', ') : 'all'})` }}
                        </span>
                    </template>
                    <v-card class="px-6 py-4" style="z-index: 10">
                        <v-row v-for="country in countries" :key="`${country.id}_country`">
                            <v-checkbox
                                :label="country.code"
                                :value="country.code"
                                v-model="selectedCountries"
                                dense
                                hide-details
                                @change="handleFilterCountryClick"
                            />
                        </v-row>
                    </v-card>
                </v-menu>
            </template>
            <template v-slot:item.status="{ item }">
                <v-tooltip bottom v-if="['error', 'warning'].includes(item.status)">
                    <template v-slot:activator="{ on }">
                        <v-chip
                            v-on="on"
                            :color="
                                probeStatusText(item) === 'active'
                                    ? 'success'
                                    : probeStatusText(item) === 'error'
                                    ? 'error'
                                    : probeStatusText(item) === 'warning'
                                    ? 'orange'
                                    : 'grey lighten-2'
                            "
                        >
                            {{ probeStatusText(item) | cleanString }}
                        </v-chip>
                    </template>
                    <p v-for="(error, i) in item.error_descriptions" :key="i">{{ error }}</p>
                </v-tooltip>
                <template v-else>
                    <v-chip
                        :color="
                            probeStatusText(item) === 'active'
                                ? 'success'
                                : probeStatusText(item) === 'error'
                                ? 'error'
                                : probeStatusText(item) === 'warning'
                                ? 'orange'
                                : 'grey lighten-2'
                        "
                    >
                        {{ probeStatusText(item) | cleanString }}
                    </v-chip>
                </template>
            </template>
            <template v-slot:item.latest_state="{ item }">
                <template>
                    <v-chip
                        v-if="probeStateText(item)"
                        :color="
                            probeStateText(item) === 'Working'
                                ? 'success'
                                : probeStateText(item) === 'Closed Fully'
                                ? 'error'
                                : probeStateText(item) === 'Maintenance'
                                ? 'orange'
                                : 'grey lighten-2'
                        "
                    >
                        {{ probeStateText(item) | cleanString }}
                    </v-chip>
                </template>
            </template>
            <template v-slot:item.use_for_map_layers="{ item }">
                <div class="flex align-center justify-center" style="width: 100%; display: flex">
                    <v-checkbox
                        v-if="['super_admin', 'admin'].includes(user.role)"
                        dense
                        hide-details
                        :ripple="false"
                        class="pt-0"
                        v-model="item.use_for_map_layers"
                        @change="handleUseForMapLayersCheckboxToggle(item)"
                    />
                </div>
            </template>
            <template v-slot:item.edit="{ item }">
                <v-btn icon color="primary" :to="{ name: 'station-view', params: { id: item.id } }">
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
            </template>
            <template v-slot:item.actions="{ item }">
                <v-btn :id="`menuActivatior-${item.id}`" color="primary" icon v-if="item.active_station_location">
                    <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>

                <v-menu :activator="`#menuActivatior-${item.id}`">
                    <v-list>
                        <v-list-item class="menu-item" @click="handleRedirectToDashboardClick(item)">
                            <v-list-item-title>
                                <v-icon color="primary" class="mr-2">mdi-monitor-dashboard</v-icon> Redirect to
                                Dashboard</v-list-item-title
                            >
                        </v-list-item>
                        <v-list-item class="menu-item" @click="handleRedirectToGraphsClick(item)">
                            <v-list-item-title
                                ><v-icon color="primary" class="mr-2">mdi-chart-bell-curve-cumulative</v-icon> Redirect
                                to Graphs</v-list-item-title
                            >
                        </v-list-item>
                        <v-list-item class="menu-item" @click="handleRedirectToOldGraphsClick(item)" v-if="isAdmin">
                            <v-list-item-title
                                ><v-icon color="primary" class="mr-2">mdi-chart-bell-curve-cumulative</v-icon> Redirect
                                to Old Graphs</v-list-item-title
                            >
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import debounce from 'debounce'

export default {
    name: 'StationListTable',
    data() {
        return {
            search: '',
            options: this.tableOptions,
            footerProps: { 'items-per-page-options': [20, 50, 100, 500] },
            statuses: ['all', 'active', 'inactive', 'error', 'warning'],
            states: [
                { name: 'All', value: 'all' },
                { name: 'Working', value: 'working' },
                { name: 'In Stock', value: 'in_stock' },
                { name: 'To Collect', value: 'to_collect' },
                { name: 'Maintenance', value: 'maintenance' },
                { name: 'Closed Replaced', value: 'closed_replaced' },
                { name: 'Closed Fully', value: 'closed_fully' },
                { name: 'Billable trial', value: 'billable_trial' },
                { name: 'Free trial', value: 'free_trial' },
                { name: 'Repair', value: 'repair' },
                { name: 'Customer stock', value: 'customer_stock' },
            ],
            showStatusMenu: false,
            showStateMenu: false,
            showCountryMenu: false,
            selectedState: 'all',
            selectedStatus: 'all',
            selectedCountry: 'all',
            selectedCountries: [],
            headers: [
                {
                    text: 'Name',
                    align: 'start',
                    sortable: false,
                    value: 'name',
                },
                {
                    text: 'Long alias',
                    align: 'start',
                    sortable: false,
                    value: 'long_alias',
                },
                {
                    text: 'Company',
                    align: 'center',
                    sortable: false,
                    value: 'company_name',
                },
                {
                    text: 'Soil type',
                    align: 'center',
                    sortable: false,
                    value: 'soil_type',
                },
                {
                    text: 'PT State',
                    align: 'center',
                    sortable: false,
                    value: 'status',
                },
                {
                    text: 'PT Condition',
                    align: 'center',
                    sortable: false,
                    value: 'latest_state',
                },
                {
                    text: 'Country',
                    align: 'center',
                    sortable: false,
                    value: 'latest_country',
                },
                {
                    text: 'Use for map layers',
                    align: 'center',
                    sortable: false,
                    value: 'use_for_map_layers',
                },
                {
                    text: '',
                    align: 'end',
                    sortable: false,
                    value: 'edit',
                },
                {
                    text: '',
                    align: 'end',
                    sortable: false,
                    value: 'actions',
                },
            ],
        }
    },
    watch: {
        async search() {
            await this.setSearchString(this.search)
            this.debouncedSearch(this.search)
        },
        options(val) {
            this.setTableOptions(val)
        },
    },
    async mounted() {
        if (!this.countries.length) {
            await this.fetchCountries();
        }
    },
    created() {
        this.setQueryStatus(this.selectedStatus)

        this.debouncedSearch = debounce(() => {
            this.setTableOptions({ ...this.tableOptions, page: 1 })
        }, 300)
    },
    computed: {
        ...mapState('probe', ['probesList', 'loading', 'tableOptions', 'probesCount', 'queryStatus', 'stationStates']),
        ...mapState('country', ['countries']),
        ...mapState('auth', ['user']),
        ...mapGetters('auth', ['isAdmin']),
    },
    methods: {
        ...mapActions('probe', ['fetchProbeList', 'toggleProbeUseForMap']),
        ...mapMutations('probe', ['setTableOptions', 'setSearchString', 'setQueryStatus', 'setStateQuery', 'setCountriesQuery']),
        ...mapActions('country', ['fetchCountries']),
        customFilter(items) {
            return items
        },
        async handleUseForMapLayersCheckboxToggle(item) {
            await this.toggleProbeUseForMap({ itemId: item.id, value: item.use_for_map_layers })
        },
        handleFilterStatusesClick() {
            this.setQueryStatus(this.selectedStatus)
            this.setTableOptions({ ...this.tableOptions, page: 1 })
        },
        handleFilterStatesClick() {
            this.setStateQuery(this.selectedState)
            this.setTableOptions({ ...this.tableOptions, page: 1 })
        },
        handleFilterCountryClick() {
            this.setCountriesQuery(this.selectedCountries.join(','))
            this.setTableOptions({ ...this.tableOptions, page: 1 })
        },
        probeStatusText(probe) {
            if (this.selectedStatus !== 'all') return this.selectedStatus
            return probe.status
        },
        probeStateText(probe) {
            if (this.selectedState !== 'all') return this.selectedState
            return probe.latest_state
        },
        handleRedirectToDashboardClick(item) {
            let url = new URL(`${process.env.VUE_APP_APP_URL}/dashboard/${item.id}`)
            window.open(url, '_blank')
        },
        handleRedirectToGraphsClick(item) {
            let alias = item.long_alias ? item.long_alias : item.alias
            let url = new URL(`${process.env.VUE_APP_APP_URL}/graph-v2/${item.active_station_location.id}`)
            window.open(url, '_blank')
        },
        handleRedirectToOldGraphsClick(item) {
            let alias = item.long_alias ? item.long_alias : item.alias
            let url = new URL(`${process.env.VUE_APP_APP_URL}/graph/${item.active_station_location.id}`)
            url.searchParams.set('alias', alias)
            window.open(url, '_blank')
        },
    },
}
</script>

<style scoped>
.menu-item {
    cursor: pointer;
}

.menu-item:hover {
    opacity: 0.7;
}
</style>
